import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import ReturnDeclarationSkuActions from './duck'
import managers from './managers'
import { RequestError } from 'modules/errors'
import { generatePath } from 'react-router-dom'
import { push } from 'connected-react-router'
import ReturnDeclarationSkuSelectors from './selectors'
import ReturnDeclarationSkuRoutes from 'views/pages/ReturnDeclarationSku/routes'
import { updateLocationQuery } from 'modules/sagaHelpers'
import { changePersistentFilters, getPersistentFilter } from 'modules/utils/helpers'
import { ListResponse } from 'types/api'
import { ReturnDeclarationSku } from './types'
import * as uuid from 'uuid'

const prepareOrder = (order: ReturnDeclarationSku): ReturnDeclarationSku => {
  order.sku_items?.forEach((item) => (item.key = uuid.v4()))
  return order
}

export const fetchList = function* (props) {
  try {
    changePersistentFilters(props.type, props.payload)
    let currentPage = yield select(ReturnDeclarationSkuSelectors.page)
    const filter = yield select(ReturnDeclarationSkuSelectors.filter)
    const persistentFilters = getPersistentFilter('season_id')
    const filterUpdated = {
      ...filter,
      ...persistentFilters,
    }

    yield put(ReturnDeclarationSkuActions.filtersUpdatedWithPersistentStorage(filterUpdated))
    const sorting = yield select(ReturnDeclarationSkuSelectors.sorting)
    const pageSize = yield select(ReturnDeclarationSkuSelectors.pageSize)

    let response: ListResponse<ReturnDeclarationSku> = yield call(
      managers.getList,
      filterUpdated,
      sorting,
      currentPage,
      pageSize,
    )
    const pages = Math.ceil(response.total_count / pageSize)

    if (pages !== 0 && pages < currentPage) {
      response = yield call(managers.getList, filterUpdated, sorting, pages, pageSize)
      currentPage = pages
    }

    const { data, page, total_count } = response
    yield put(
      ReturnDeclarationSkuActions.listRequestSucceed(
        data.map(prepareOrder),
        total_count,
        response.meta.total_qty,
        response.meta.total_final_qty,
        response.meta.total_delivered_qty,
        response.meta.total_standard_qty,
        page,
      ),
    )

    if (currentPage) {
      yield call(updateLocationQuery, ReturnDeclarationSkuRoutes.List, { page: currentPage })
    }
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(ReturnDeclarationSkuActions.listRequestFailed(errType))
  }
}

export const fetchListUpdate = function* () {
  try {
    const currentPage = yield select(ReturnDeclarationSkuSelectors.page)
    const filter = yield select(ReturnDeclarationSkuSelectors.filter)
    const sorting = yield select(ReturnDeclarationSkuSelectors.sorting)
    const pageSize = yield select(ReturnDeclarationSkuSelectors.pageSize)
    const response: ListResponse<ReturnDeclarationSku> = yield call(
      managers.getList,
      filter,
      sorting,
      currentPage || 1,
      pageSize,
    )
    const { data, total_count } = response
    yield put(ReturnDeclarationSkuActions.listUpdateRequestSucceed(data, total_count))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(ReturnDeclarationSkuActions.listUpdateRequestFailed(errType))
  }
}

export const fetchListNext = function* () {
  try {
    const page = yield select(ReturnDeclarationSkuSelectors.page)
    const filter = yield select(ReturnDeclarationSkuSelectors.filter)
    const sorting = yield select(ReturnDeclarationSkuSelectors.sorting)
    const pageSize = yield select(ReturnDeclarationSkuSelectors.pageSize)
    const { data, total_count }: { data: ReturnDeclarationSku[]; total_count: number } = yield call(
      managers.getList,
      filter,
      sorting,
      page,
      pageSize,
    )
    yield put(ReturnDeclarationSkuActions.listRequestNextSucceed(data, total_count))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(ReturnDeclarationSkuActions.listRequestNextFailed(errType))
  }
}

export const fetchItem = function* ({ payload: id }: ReturnType<typeof ReturnDeclarationSkuActions.itemRequested>) {
  try {
    const order: ReturnDeclarationSku = yield call(managers.getItem, id)
    yield put(ReturnDeclarationSkuActions.itemRequestSucceed(prepareOrder(order)))
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(ReturnDeclarationSkuActions.itemRequestFailed(id, errType))
  }
}

export const addItem = function* ({ payload }: ReturnType<typeof ReturnDeclarationSkuActions.addRequested>) {
  try {
    let duplicate = false
    let dto

    if (Array.isArray(payload)) {
      dto = payload[0]
      duplicate = payload[1]
    } else {
      dto = payload
    }
    const order: ReturnDeclarationSku = yield call(managers.addItem, dto)
    yield put(ReturnDeclarationSkuActions.addSucceed(prepareOrder(order)))
    if (!duplicate) {
      yield put(push(generatePath(ReturnDeclarationSkuRoutes.Edit, { id: order.id })))
    }
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(ReturnDeclarationSkuActions.addFailed(errType))
  }
}

export const updateItem = function* ({
  payload: [id, dto, duplicate],
}: ReturnType<typeof ReturnDeclarationSkuActions.updateRequested>) {
  try {
    const order: ReturnDeclarationSku = yield call(managers.updateItem, id, dto)
    yield put(ReturnDeclarationSkuActions.updateSucceed(prepareOrder(order)))
    if (duplicate) {
      yield put(push(ReturnDeclarationSkuRoutes.Add))
    }
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(ReturnDeclarationSkuActions.updateFailed(id, errType))
  }
}

export const removeItem = function* ({ payload }: ReturnType<typeof ReturnDeclarationSkuActions.removeRequested>) {
  try {
    yield call(managers.removeItem, payload)
    yield put(ReturnDeclarationSkuActions.removeSucceed(payload))
    yield put(push(ReturnDeclarationSkuRoutes.List))
    yield put(ReturnDeclarationSkuActions.resetRemoveProgress())
  } catch (err) {
    const errType = err instanceof RequestError ? err.type : 'unknown'
    yield put(ReturnDeclarationSkuActions.removeFailed(payload, errType))
  }
}

export const printDeclaration = function* ({ payload }: ReturnType<typeof ReturnDeclarationSkuActions.printRequested>) {
  try {
    yield call(managers.printDeclaration, payload)
    yield put(ReturnDeclarationSkuActions.printSucceed())
  } catch (err) {
    const { type } = RequestError.parseError(err)
    yield put(ReturnDeclarationSkuActions.printFailed(type))
  }
}

export const fetchDeclarationCount = function* ({
  payload,
}: ReturnType<typeof ReturnDeclarationSkuActions.countRequested>) {
  try {
    const declarationCount = yield call(managers.getCount, payload)
    yield put(ReturnDeclarationSkuActions.countRequestedSucceed(declarationCount))
  } catch (err) {
    const { errors } = RequestError.parseError(err)
    yield put(ReturnDeclarationSkuActions.countRequestedFailed(errors))
  }
}

const ReturnDeclarationSkuSaga = function* () {
  yield all([
    takeLatest(ReturnDeclarationSkuActions.listRequested.type, fetchList),
    takeLatest(ReturnDeclarationSkuActions.filterUpdated.type, fetchList),
    takeLatest(ReturnDeclarationSkuActions.sortingUpdated.type, fetchList),
    takeLatest(ReturnDeclarationSkuActions.filterHasBeenReset.type, fetchList),
    takeLatest(ReturnDeclarationSkuActions.sortingHasBeenReset.type, fetchList),
    takeLatest(ReturnDeclarationSkuActions.listRequestedNext.type, fetchListNext),

    takeLatest(ReturnDeclarationSkuActions.itemRequested.type, fetchItem),
    takeLatest(ReturnDeclarationSkuActions.addRequested.type, addItem),
    takeLatest(ReturnDeclarationSkuActions.updateRequested.type, updateItem),
    takeLatest(ReturnDeclarationSkuActions.removeRequested.type, removeItem),

    takeLatest(ReturnDeclarationSkuActions.printRequested.type, printDeclaration),
    takeLatest(ReturnDeclarationSkuActions.countRequested.type, fetchDeclarationCount),
  ])
}

export default ReturnDeclarationSkuSaga
